import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Heart, Sparkles, Shield, Users } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { login, register, guestLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(loginData.email, loginData.password);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await register(registerData.email, registerData.password, registerData.name);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await guestLogin();
      navigate('/onboarding');
    } catch (err) {
      setError('Guest login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain overflow-x-hidden">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sage-100/50 via-transparent to-terracotta-100/30" />
        
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-16 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Hero Content */}
            <div className="space-y-6 sm:space-y-8 animate-fade-in text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                Your Mental Wellness Partner
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-foreground leading-tight">
                Your Mental Health,
                <span className="block text-primary">Our Priority.</span>
              </h1>
              
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-lg mx-auto lg:mx-0">
                TPOCARE AI connects you with compassionate support bridging therapy, 
                psychiatry, and wellness resources—all in one safe, confidential space.
              </p>

              {/* Features */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4 max-w-md mx-auto lg:mx-0">
                {[
                  { icon: Heart, label: '24/7 Support' },
                  { icon: Shield, label: 'Confidential' },
                  { icon: Sparkles, label: 'AI-Assisted' },
                  { icon: Users, label: 'Professional Care' },
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-card shadow-soft">
                    <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-primary/10">
                      <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-foreground">{feature.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Auth Card */}
            <div className="animate-slide-up">
              <Card className="shadow-floating border-0 bg-card/80 backdrop-blur-xl">
                <CardHeader className="text-center pb-2 px-4 sm:px-6">
                  <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
                    <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-heading">Welcome to TPOCARE</CardTitle>
                  <CardDescription className="text-sm">Start your journey to emotional wellness</CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                  {error && (
                    <div className="p-2.5 sm:p-3 rounded-xl bg-destructive/10 text-destructive text-xs sm:text-sm text-center" data-testid="auth-error">
                      {error}
                    </div>
                  )}

                  <Tabs defaultValue="login" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-muted/50">
                      <TabsTrigger value="login" className="text-sm" data-testid="login-tab">Sign In</TabsTrigger>
                      <TabsTrigger value="register" className="text-sm" data-testid="register-tab">Sign Up</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="login" className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                      <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
                        <div className="space-y-1.5 sm:space-y-2">
                          <Label htmlFor="login-email" className="text-sm">Email</Label>
                          <Input
                            id="login-email"
                            type="email"
                            placeholder="your@email.com"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            className="bg-input border-0 rounded-xl h-10 sm:h-12 text-sm"
                            data-testid="login-email-input"
                            required
                          />
                        </div>
                        <div className="space-y-1.5 sm:space-y-2">
                          <Label htmlFor="login-password" className="text-sm">Password</Label>
                          <Input
                            id="login-password"
                            type="password"
                            placeholder="••••••••"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            className="bg-input border-0 rounded-xl h-10 sm:h-12 text-sm"
                            data-testid="login-password-input"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full h-10 sm:h-12 rounded-full btn-hover text-sm"
                          disabled={isLoading}
                          data-testid="login-submit-btn"
                        >
                          {isLoading ? 'Signing in...' : 'Sign In'}
                        </Button>
                      </form>
                    </TabsContent>

                    <TabsContent value="register" className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                      <form onSubmit={handleRegister} className="space-y-3 sm:space-y-4">
                        <div className="space-y-1.5 sm:space-y-2">
                          <Label htmlFor="register-name" className="text-sm">Name</Label>
                          <Input
                            id="register-name"
                            type="text"
                            placeholder="Your name"
                            value={registerData.name}
                            onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                            className="bg-input border-0 rounded-xl h-10 sm:h-12 text-sm"
                            data-testid="register-name-input"
                            required
                          />
                        </div>
                        <div className="space-y-1.5 sm:space-y-2">
                          <Label htmlFor="register-email" className="text-sm">Email</Label>
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="your@email.com"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            className="bg-input border-0 rounded-xl h-10 sm:h-12 text-sm"
                            data-testid="register-email-input"
                            required
                          />
                        </div>
                        <div className="space-y-1.5 sm:space-y-2">
                          <Label htmlFor="register-password" className="text-sm">Password</Label>
                          <Input
                            id="register-password"
                            type="password"
                            placeholder="••••••••"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            className="bg-input border-0 rounded-xl h-10 sm:h-12 text-sm"
                            data-testid="register-password-input"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full h-10 sm:h-12 rounded-full btn-hover text-sm"
                          disabled={isLoading}
                          data-testid="register-submit-btn"
                        >
                          {isLoading ? 'Creating account...' : 'Create Account'}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full h-10 sm:h-12 rounded-full border-2 btn-hover text-sm"
                    onClick={handleGuestLogin}
                    disabled={isLoading}
                    data-testid="guest-login-btn"
                  >
                    Continue as Guest
                  </Button>

                  <p className="text-xs text-center text-muted-foreground px-2">
                    By continuing, you agree that this is an AI companion for emotional support, 
                    not a replacement for professional mental health care.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
